import { useEffect, useState } from 'react';
import { uploadFiles, UploadedFile, SimplifiedIAPIClient } from '../';

interface UseFileUploadParams {
  onUploadedFile: (uploadState: UploadedFile) => void;
  accept?: string;
  API: () => SimplifiedIAPIClient;
  captureException: (...args: unknown[]) => void;
  /**
   * Optional gate run against the chosen file before it is uploaded. Return
   * `false` (or a promise resolving to `false`) to abort the upload — e.g. when
   * the file fails client-side validation. Defaults to allowing the upload.
   */
  beforeUpload?: (file: File) => boolean | Promise<boolean>;
}

interface UseFileUploadReturn {
  chooseFile: () => void;
}

export function useFileUpload({
  onUploadedFile,
  accept,
  API,
  captureException,
  beforeUpload,
}: UseFileUploadParams): UseFileUploadReturn {
  const [fileUploadState, setFileUploadState] = useState<UploadedFile>();

  useEffect(() => {
    if (fileUploadState) {
      onUploadedFile(fileUploadState);
      setFileUploadState(undefined);
    }
  }, [fileUploadState, onUploadedFile]);

  const chooseFile = () => {
    const input = document.createElement('input');

    input.type = 'file';
    if (accept) {
      input.accept = accept;
    }
    input.id = 'file-upload-hook-input';
    document.body.appendChild(input);

    const handleChange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target?.files?.[0];

      const cleanup = () => {
        target.removeEventListener('change', handleChange);
        target.remove();
      };

      if (!file) {
        cleanup();
        return;
      }

      if (beforeUpload && !(await beforeUpload(file))) {
        cleanup();
        return;
      }

      const uploadResult = await uploadFiles([file], API, captureException);
      setFileUploadState(uploadResult[0]);

      cleanup();
    };
    input.addEventListener('change', handleChange);
    input.click();
  };

  return { chooseFile };
}
